/**
 * 투어 가이드 기능 - driver.js를 사용한 SNS 애플리케이션 사용 가이드
 * 
 * 이 모듈은 driver.js 라이브러리를 활용하여 새 사용자에게 
 * 애플리케이션의 핵심 기능과 인터페이스 요소를 소개하는 투어 가이드를 제공합니다.
 */

import { driver as Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

// 다크 모드 감지 함수
const isDarkMode = () => {
  return document.body.getAttribute('data-theme') === 'dark' || 
         document.body.classList.contains('dark-mode');
};

// 다크 모드에 맞는 스타일 설정
const getDarkModeStyles = () => {
  if (isDarkMode()) {
    return {
      backgroundColor: '#242526',
      color: '#E4E6EB',
      borderColor: '#3E4042'
    };
  }
  
  return {
    backgroundColor: '#ffffff',
    color: '#333333',
    borderColor: '#e4e4e4'
  };
};

/**
 * 메인 투어 가이드 클래스
 * 각 기능별로 투어를 제공하는 메서드를 포함합니다.
 */
class TourGuide {
  constructor() {
    this.driver = null;
    this.tourHistory = {};
    this.currentStepIndex = 0;
    this.steps = [];
    this.autoAdvance = false;
    this.autoAdvanceDelay = 3000; // 자동 진행 간격 (밀리초)
    this.autoAdvanceTimer = null;
    this.initializeDriver();
    this.loadTourHistory();
  }
  
  /**
   * driver.js 초기화
   */
  initializeDriver() {
    const styles = getDarkModeStyles();
    
    this.driver = new Driver({
      animate: true,
      opacity: 0.6,
      padding: 10,
      allowClose: true,
      overlayClickNext: false,
      doneBtnText: '완료',
      closeBtnText: '닫기',
      nextBtnText: '다음',
      prevBtnText: '이전',
      showButtons: true,
      keyboardControl: true,
      stagePadding: 0,
      popoverClass: 'sns-tour-popover',
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      onHighlightStarted: (element) => {
        // 하이라이트 시작 시 동작
        console.log('하이라이트 시작:', element);
      },
      onHighlighted: (element) => {
        // 하이라이트 완료 시 동작
        console.log('하이라이트 완료:', element);
        
        // 팝오버에 클릭 이벤트 추가 (클릭하면 다음 단계로 이동)
        setTimeout(() => {
          const popover = document.querySelector('.driver-popover');
          if (popover) {
            popover.style.cursor = 'pointer';
            
            // 이미 추가된 클릭 이벤트가 있다면 제거
            popover.removeEventListener('click', this._popoverClickHandler);
            
            // 새 클릭 이벤트 추가
            this._popoverClickHandler = (e) => {
              // 버튼 영역 클릭은 무시 (버튼 자체의 클릭 이벤트 발생)
              if (!e.target.closest('.driver-popover-footer')) {
                this.driver.moveNext();
              }
            };
            
            // 이벤트 리스너 추가
            popover.addEventListener('click', this._popoverClickHandler);
          }
        }, 100);
        
        // 자동 진행 모드일 경우 타이머 시작
        if (this.autoAdvance) {
          this.startAutoAdvanceTimer();
        }
      },
      onDeselected: (element) => {
        // 하이라이트 제거 시 동작
        console.log('하이라이트 제거:', element);
        
        // 자동 진행 모드일 경우 타이머 중지
        if (this.autoAdvance) {
          this.stopAutoAdvanceTimer();
        }
      },
      onReset: () => {
        // 투어 종료 시 동작
        console.log('투어 종료');
        this.stopAutoAdvanceTimer();
        this.currentStepIndex = 0;
      }
    });
    
    // 호환성을 위한 래퍼 메서드 추가
    if (!this.driver.defineSteps) {
      this.driver.defineSteps = (steps) => {
        this._steps = steps;
        this.steps = steps;
      };
    }
    
    if (!this.driver.start) {
      this.driver.start = () => {
        if (this._steps && this._steps.length > 0) {
          this.currentStepIndex = 0;
          this.driver.highlight(this._steps[this.currentStepIndex]);
        }
      };
    }
    
    // reset 메서드가 없는 경우 래퍼 추가
    if (!this.driver.reset) {
      this.driver.reset = () => {
        // 최신 driver.js에서는 isActivated() 등의 메서드로 활성화 상태 확인 가능
        if (typeof this.driver.isActivated === 'function' && this.driver.isActivated()) {
          // 활성화된 경우 투어 종료
          try {
            // 현재 하이라이트된 요소가 있으면 제거
            this.driver.getHighlightedElement && this.driver.getHighlightedElement()?.hide();
            // 오버레이 제거
            this.driver.getOverlay && this.driver.getOverlay()?.hide();
            // 팝오버 제거
            this.driver.getPopover && this.driver.getPopover()?.hide();
          } catch (e) {
            console.warn('투어 종료 중 오류 발생:', e);
          }
        }
        
        // 자체 이벤트 트리거
        if (this.driver.onReset) {
          this.driver.onReset();
        }
        
        // 타이머 중지 및 상태 초기화
        this.stopAutoAdvanceTimer();
        this.currentStepIndex = 0;
      };
    }
    
    // 사용자 정의 다음 단계 메서드 추가
    this.driver.moveNext = () => {
      if (this._steps && this._steps.length > 0) {
        this.currentStepIndex++;
        if (this.currentStepIndex < this._steps.length) {
          this.driver.highlight(this._steps[this.currentStepIndex]);
        } else {
          // 투어 종료 처리
          this.driver.reset();
          // 종료 후 상태 초기화
          this.currentStepIndex = 0;
          this.stopAutoAdvanceTimer();
        }
      }
    };
    
    // 커스텀 CSS 추가
    this.addCustomCSS(styles);
  }
  
  /**
   * 자동 진행 설정
   * @param {boolean} enabled - 자동 진행 활성화 여부
   * @param {number} delay - 지연 시간(밀리초)
   */
  setAutoAdvance(enabled, delay = 3000) {
    this.autoAdvance = enabled;
    this.autoAdvanceDelay = delay;
    
    if (!enabled) {
      this.stopAutoAdvanceTimer();
    }
  }
  
  /**
   * 자동 진행 타이머 시작
   */
  startAutoAdvanceTimer() {
    // 기존 타이머가 있다면 중지
    this.stopAutoAdvanceTimer();
    
    // 새 타이머 시작
    this.autoAdvanceTimer = setTimeout(() => {
      this.driver.moveNext();
    }, this.autoAdvanceDelay);
  }
  
  /**
   * 자동 진행 타이머 중지
   */
  stopAutoAdvanceTimer() {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
  }
  
  /**
   * 투어 이력 로드
   */
  loadTourHistory() {
    const tourHistoryStr = localStorage.getItem('sns-tour-history');
    if (tourHistoryStr) {
      try {
        this.tourHistory = JSON.parse(tourHistoryStr);
      } catch (e) {
        console.error('투어 이력 파싱 오류:', e);
        this.tourHistory = {};
      }
    }
  }
  
  /**
   * 투어 이력 저장
   * @param {string} tourName - 투어 이름
   */
  saveTourHistory(tourName) {
    this.tourHistory[tourName] = {
      completed: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('sns-tour-history', JSON.stringify(this.tourHistory));
  }
  
  /**
   * 투어가 이미 완료되었는지 확인
   * @param {string} tourName - 투어 이름
   * @returns {boolean} 투어 완료 여부
   */
  isTourCompleted(tourName) {
    return this.tourHistory[tourName]?.completed === true;
  }
  
  /**
   * 투어 이력 초기화
   */
  resetTourHistory() {
    this.tourHistory = {};
    localStorage.removeItem('sns-tour-history');
    console.log('투어 이력이 초기화되었습니다.');
  }
  
  /**
   * 애플리케이션 시작 시 홈 화면 투어
   * 첫 방문자를 위한 기본 투어입니다.
   * @param {boolean} autoAdvance - 자동 진행 여부
   * @param {number} delay - 자동 진행 지연 시간(밀리초)
   */
  startHomeTour(autoAdvance = false, delay = 1000) {
    this.currentTour = 'home';
    
    // 드라이버 인스턴스 생성
    const driver = this.createDriver('홈 투어');
    
    // 투어 단계 정의
    const steps = [
      {
        element: '.navbar-brand',
        popover: {
          title: 'SNS 로고',
          description: '이곳을 클릭하면 언제든지 홈 화면으로 돌아올 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#navbarNav',
        popover: {
          title: '네비게이션 메뉴',
          description: '홈, 친구, 알림 등 다양한 기능에 접근할 수 있는 메뉴입니다.',
          position: 'bottom'
        }
      },
      {
        element: '#check-updates-btn',
        popover: {
          title: '업데이트 확인',
          description: '앱의 최신 버전이 있는지 확인하고 업데이트할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#login-btn, #user-profile',
        popover: {
          title: '로그인 / 사용자 정보',
          description: '로그인하여 더 많은 기능을 이용하거나, 로그인 후 내 정보를 확인할 수 있습니다.',
          position: 'bottom-left'
        }
      },
      {
        element: '#theme-toggle',
        popover: {
          title: '테마 전환',
          description: '밝은 테마와 어두운 테마를 전환할 수 있습니다.',
          position: 'bottom-right'
        }
      },
      {
        element: '.font-size-control',
        popover: {
          title: '글자 크기 조절',
          description: '읽기 편한 글자 크기로 조절할 수 있습니다.',
          position: 'bottom-right'
        }
      },
      {
        element: '#post-create-card, #login-card',
        popover: {
          title: '게시물 작성',
          description: '여기에서 게시물을 작성할 수 있습니다. 로그인이 필요한 기능입니다.',
          position: 'top'
        }
      },
      {
        element: '#posts-container',
        popover: {
          title: '게시물 목록',
          description: '다른 사용자들의 게시물을 확인할 수 있습니다.',
          position: 'top'
        }
      },
      {
        element: '.card:has(.role-badge)',
        popover: {
          title: '게시물 권한',
          description: '게시물에 표시된 배지는 해당 게시물에 대한 사용자의 권한을 나타냅니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물을 선택
          const posts = document.querySelectorAll('.post-card');
          if (posts.length > 0) {
            return posts[0]; // 첫 번째 게시물 반환
          }
          return null; // 게시물이 없는 경우
        }
      },
      {
        element: '#fab-container',
        popover: {
          title: '더보기 메뉴',
          description: '이 버튼을 클릭하여 웹 패널 만들기 등의 추가 기능을 사용할 수 있습니다.',
          position: 'left'
        }
      },
      {
        element: '#floating-panel-btn',
        popover: {
          title: '웹 패널 생성',
          description: '이 버튼을 클릭하면 새로운 웹 패널을 생성하여 웹페이지를 열거나 콘텐츠를 표시할 수 있습니다.',
          position: 'left'
        }
      },
      {
        element: '#fab-device-button',
        popover: {
          title: '기기 목록 조회',
          description: '이 버튼을 클릭하면 Firebase의 DEVICE 노드에 등록된 기기 목록을 조회하여 실시간으로 상태를 확인할 수 있습니다.',
          position: 'left'
        }
      }
    ];
    
    // 투어 시작
    this.driver.defineSteps(steps);
    this.driver.start();
    
    // 투어 이력 저장
    this.saveTourHistory('home-tour');
  }
  
  /**
   * 게시물 기능 투어
   * 게시물 작성, 수정, 삭제 및 공유 기능을 소개합니다.
   * @param {boolean} autoAdvance - 자동 진행 여부
   * @param {number} delay - 자동 진행 지연 시간(밀리초)
   */
  startPostTour(autoAdvance = false, delay = 3000) {
    if (this.isTourCompleted('post-tour') && !confirm('게시물 투어를 다시 보시겠습니까?')) {
      return;
    }
    
    // 자동 진행 설정
    this.setAutoAdvance(autoAdvance, delay);
    
    // 다크 모드에 맞게 스타일 업데이트
    this.addCustomCSS(getDarkModeStyles());
    
    // 투어 단계 정의
    const steps = [
      {
        element: '#post-create-card',
        popover: {
          title: '게시물 작성',
          description: '여기에서 새 게시물을 작성할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#post-content',
        popover: {
          title: '게시물 내용 작성',
          description: '생각이나 소식을 입력하세요.',
          position: 'bottom'
        }
      },
      {
        element: '#upload-image-btn',
        popover: {
          title: '이미지 업로드',
          description: '게시물에 이미지를 첨부할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#create-post-btn',
        popover: {
          title: '게시',
          description: '작성한 게시물을 공유합니다.',
          position: 'bottom-left'
        }
      },
      {
        element: '.post-action-btn.like-btn',
        popover: {
          title: '좋아요',
          description: '게시물에 좋아요를 표시할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물의 좋아요 버튼 선택
          const likeBtn = document.querySelector('.post-action-btn');
          return likeBtn;
        }
      },
      {
        element: '.post-action-btn.comment-btn',
        popover: {
          title: '댓글',
          description: '게시물에 댓글을 작성할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물의 댓글 버튼 선택
          const commentBtn = document.querySelector('.post-action-btn.comment-btn');
          return commentBtn;
        }
      },
      {
        element: '.post-action-btn.share-btn',
        popover: {
          title: '공유',
          description: '게시물을 다른 사람과 공유할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물의 공유 버튼 선택
          const shareBtn = document.querySelector('.post-action-btn.share-btn');
          return shareBtn;
        }
      },
      {
        element: '.post-action-btn.chat-btn',
        popover: {
          title: '채팅',
          description: '게시물에 관한 대화를 시작할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물의 채팅 버튼 선택
          const chatBtn = document.querySelector('.post-action-btn.chat-btn');
          return chatBtn;
        }
      },
      {
        element: '.role-badge',
        popover: {
          title: '권한 배지',
          description: '이 배지는 게시물에 대한 사용자의 권한을 나타냅니다. 클릭하여 권한을 관리할 수 있습니다.',
          position: 'bottom'
        },
        onHighlightStarted: () => {
          // 실제 게시물이 로드되었는지 확인하고 첫 번째 게시물의 권한 배지 선택
          const roleBadge = document.querySelector('.role-badge');
          return roleBadge;
        }
      }
    ];
    
    // 투어 시작
    this.driver.defineSteps(steps);
    this.driver.start();
    
    // 투어 이력 저장
    this.saveTourHistory('post-tour');
  }
  
  /**
   * 권한 기능 투어
   * 게시물 권한 관리 기능을 소개합니다.
   * @param {boolean} autoAdvance - 자동 진행 여부
   * @param {number} delay - 자동 진행 지연 시간(밀리초)
   */
  startPermissionsTour(autoAdvance = false, delay = 3000) {
    if (this.isTourCompleted('permissions-tour') && !confirm('권한 관리 투어를 다시 보시겠습니까?')) {
      return;
    }
    
    // 자동 진행 설정
    this.setAutoAdvance(autoAdvance, delay);
    
    // 다크 모드에 맞게 스타일 업데이트
    this.addCustomCSS(getDarkModeStyles());
    
    // 투어 단계 정의
    const steps = [
      {
        element: '.role-badge-clickable',
        popover: {
          title: '권한 관리',
          description: '이 배지를 클릭하여, 게시물 권한을 관리할 수 있습니다.',
          position: 'bottom'
        },
        onHighlightStarted: () => {
          // 첫 번째 게시물의 권한 배지 선택
          const roleBadge = document.querySelector('.role-badge-clickable');
          return roleBadge;
        }
      },
      {
        element: '#permissionModal',
        popover: {
          title: '권한 모달',
          description: '이 모달에서 사용자 권한을 관리할 수 있습니다.',
          position: 'center'
        },
        onPopoverRenderered: (popover, { element }) => {
          // 모달이 열려있는지 확인하고, 닫혀있다면 열기
          const permissionModal = document.querySelector('#permissionModal');
          if (permissionModal && !permissionModal.classList.contains('show')) {
            // 권한 배지 클릭하여 모달 열기
            const roleBadge = document.querySelector('.role-badge-clickable');
            if (roleBadge) {
              roleBadge.click();
            }
          }
        }
      },
      {
        element: '.permissions-list',
        popover: {
          title: '권한 목록',
          description: '현재 게시물에 접근 권한이 있는 사용자 목록입니다.',
          position: 'top'
        }
      },
      {
        element: '.delete-permission-btn',
        popover: {
          title: '권한 삭제',
          description: '사용자의 게시물 접근 권한을 삭제할 수 있습니다.',
          position: 'left'
        },
        onHighlightStarted: () => {
          // 첫 번째 삭제 버튼 선택
          const deleteBtn = document.querySelector('.delete-permission-btn');
          return deleteBtn;
        }
      },
      {
        element: '.permission-item',
        popover: {
          title: '권한 항목',
          description: '각 사용자의 이메일과 권한 레벨을 확인할 수 있습니다.',
          position: 'bottom'
        },
        onHighlightStarted: () => {
          // 첫 번째 권한 항목 선택
          const permissionItem = document.querySelector('.permission-item');
          return permissionItem;
        }
      },
      {
        element: '#permission-email',
        popover: {
          title: '사용자 추가',
          description: '이메일을 입력하여 새 사용자에게 권한을 부여할 수 있습니다.',
          position: 'top'
        }
      },
      {
        element: '#permission-role',
        popover: {
          title: '권한 레벨 선택',
          description: '사용자에게 부여할 권한 레벨을 선택합니다.',
          position: 'top'
        }
      },
      {
        element: '#add-permission-btn',
        popover: {
          title: '권한 추가',
          description: '입력한 사용자에게 선택한 권한을 부여합니다.',
          position: 'top'
        }
      }
    ];
    
    // 투어 시작
    this.driver.defineSteps(steps);
    this.driver.start();
    
    // 투어 이력 저장
    this.saveTourHistory('permissions-tour');
  }
  
  /**
   * 웹 패널 기능 투어
   * 웹 패널 기능을 소개합니다.
   * @param {boolean} autoAdvance - 자동 진행 여부
   * @param {number} delay - 자동 진행 지연 시간(밀리초)
   */
  startPanelTour(autoAdvance = false, delay = 3000) {
    if (this.isTourCompleted('panel-tour') && !confirm('웹 패널 투어를 다시 보시겠습니까?')) {
      return;
    }
    
    // 자동 진행 설정
    this.setAutoAdvance(autoAdvance, delay);
    
    // 다크 모드에 맞게 스타일 업데이트
    this.addCustomCSS(getDarkModeStyles());
    
    // 투어 단계 정의
    const steps = [
      {
        element: '#fab-main-button',
        popover: {
          title: '더보기 메뉴',
          description: '이 버튼을 클릭하여 웹 패널 관련 기능을 사용할 수 있습니다.',
          position: 'left'
        }
      },
      {
        element: '#floating-panel-btn',
        popover: {
          title: '새 웹 패널',
          description: '클릭하여 새로운 웹 패널을 생성할 수 있습니다.',
          position: 'left'
        },
        onPopoverRenderered: (popover, { element }) => {
          // FAB 메뉴가 열려있는지 확인하고, 닫혀있다면 열기
          const fabContainer = document.querySelector('#fab-container');
          if (fabContainer && !fabContainer.classList.contains('open')) {
            // FAB 버튼 클릭하여 메뉴 열기
            const fabButton = document.querySelector('#fab-main-button');
            if (fabButton) {
              fabButton.click();
            }
          }
        }
      },
      {
        element: '#open-panel-manager',
        popover: {
          title: '패널 관리',
          description: '클릭하여 패널 관리 메뉴를 열 수 있습니다.',
          position: 'left'
        }
      },
      {
        element: '.draggable-panel',
        popover: {
          title: '웹 패널',
          description: '웹 브라우저처럼 사용할 수 있는 패널입니다. 드래그하여 위치를 변경할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          // 첫 번째 패널 선택
          const panel = document.querySelector('.draggable-panel');
          
          // 패널이 없다면 새로 생성
          if (!panel) {
            const createPanelBtn = document.querySelector('#floating-panel-btn');
            if (createPanelBtn) {
              createPanelBtn.click();
              
              // 패널이 생성될 때까지 기다린 후 반환
              return new Promise((resolve) => {
                setTimeout(() => {
                  resolve(document.querySelector('.draggable-panel'));
                }, 500);
              });
            }
          }
          
          return panel;
        }
      },
      {
        element: '.panel-titlebar',
        popover: {
          title: '패널 타이틀바',
          description: '이 영역을 드래그하여 패널을 이동할 수 있습니다.',
          position: 'top'
        },
        onHighlightStarted: () => {
          return document.querySelector('.panel-titlebar');
        }
      },
      {
        element: '.panel-url-input',
        popover: {
          title: 'URL 입력',
          description: '방문할 웹사이트 주소를 입력할 수 있습니다.',
          position: 'bottom'
        },
        onHighlightStarted: () => {
          return document.querySelector('.panel-url-input');
        }
      },
      {
        element: '.panel-url-go-btn',
        popover: {
          title: '접속',
          description: '입력한 URL로 이동합니다.',
          position: 'bottom'
        },
        onHighlightStarted: () => {
          return document.querySelector('.panel-url-go-btn');
        }
      },
      {
        element: '.panel-controls',
        popover: {
          title: '패널 제어',
          description: '패널을 최소화, 최대화, 닫기 등의 제어를 할 수 있습니다.',
          position: 'bottom-right'
        },
        onHighlightStarted: () => {
          return document.querySelector('.panel-controls');
        }
      },
      {
        element: '.panel-resize-handle.se',
        popover: {
          title: '크기 조절',
          description: '이 핸들을 드래그하여 패널 크기를 조절할 수 있습니다.',
          position: 'top-left'
        },
        onHighlightStarted: () => {
          return document.querySelector('.panel-resize-handle.se');
        }
      }
    ];
    
    // 투어 시작
    this.driver.defineSteps(steps);
    this.driver.start();
    
    // 투어 이력 저장
    this.saveTourHistory('panel-tour');
  }
  
  /**
   * 설정 기능 투어
   * 설정 메뉴를 소개합니다.
   * @param {boolean} autoAdvance - 자동 진행 여부
   * @param {number} delay - 자동 진행 지연 시간(밀리초)
   */
  startSettingsTour(autoAdvance = false, delay = 3000) {
    if (this.isTourCompleted('settings-tour') && !confirm('설정 투어를 다시 보시겠습니까?')) {
      return;
    }
    
    // 자동 진행 설정
    this.setAutoAdvance(autoAdvance, delay);
    
    // 다크 모드에 맞게 스타일 업데이트
    this.addCustomCSS(getDarkModeStyles());
    
    // 투어 단계 정의
    const steps = [
      {
        element: '#open-settings-modal',
        popover: {
          title: '설정 메뉴',
          description: '이 버튼을 클릭하여 설정 메뉴를 열 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#settingsModal',
        popover: {
          title: '설정 모달',
          description: '다양한 설정을 변경할 수 있는 모달입니다.',
          position: 'center'
        },
        onPopoverRenderered: (popover, { element }) => {
          // 모달이 열려있는지 확인하고, 닫혀있다면 열기
          const settingsModal = document.querySelector('#settingsModal');
          if (settingsModal && !settingsModal.classList.contains('show')) {
            // 설정 버튼 클릭하여 모달 열기
            const settingsBtn = document.querySelector('#open-settings-modal');
            if (settingsBtn) {
              settingsBtn.click();
            }
          }
        }
      },
      {
        element: '#settingsTabs',
        popover: {
          title: '설정 탭',
          description: '여러 카테고리의 설정을 확인할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#system-tab',
        popover: {
          title: '시스템 설정',
          description: '언어, 시간대, 알림 설정 등 시스템 관련 설정을 변경할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#environment-tab',
        popover: {
          title: '환경 설정',
          description: '다크 모드, 글자 크기, 레이아웃 등 화면 환경을 설정할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#menu-tab',
        popover: {
          title: '메뉴 설정',
          description: '메뉴 표시 여부와 기본 페이지를 설정할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#other-tab',
        popover: {
          title: '기타 설정',
          description: '앱 정보 확인, 캐시 삭제, 설정 초기화 등 기타 기능을 사용할 수 있습니다.',
          position: 'bottom'
        }
      },
      {
        element: '#saveSettingsBtn',
        popover: {
          title: '설정 저장',
          description: '변경한 설정을 저장합니다.',
          position: 'top'
        }
      }
    ];
    
    // 투어 시작
    this.driver.defineSteps(steps);
    this.driver.start();
    
    // 투어 이력 저장
    this.saveTourHistory('settings-tour');
  }
  
  /**
   * 다크 모드에 맞는 커스텀 CSS 추가
   */
  addCustomCSS(styles) {
    // 이미 존재하는 스타일 태그가 있는지 확인
    let styleTag = document.getElementById('driver-js-custom-styles');
    
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'driver-js-custom-styles';
      document.head.appendChild(styleTag);
    }
    
    // 다크 모드 또는 라이트 모드에 따른 커스텀 스타일 적용
    styleTag.innerHTML = `
      .driver-popover {
        background-color: ${styles.backgroundColor} !important;
        color: ${styles.color} !important;
        border-color: ${styles.borderColor} !important;
        position: relative;
        cursor: pointer;
      }
      
      .driver-popover:after {
        content: '클릭하여 다음으로 이동';
        position: absolute;
        bottom: 5px;
        right: 110px;
        font-size: 11px;
        color: #888;
        font-style: italic;
      }
      
      .driver-popover-title {
        color: ${styles.color} !important;
      }
      
      .driver-popover-description {
        color: ${styles.color} !important;
      }
      
      .driver-popover-footer button {
        border-color: ${styles.borderColor} !important;
      }
      
      .driver-popover-progress-text {
        color: ${styles.color} !important;
      }
      
      .driver-highlighted-element {
        outline: 2px solid #3B5998 !important;
      }
    `;
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const tourGuide = new TourGuide();
export default tourGuide; 